import React, { useState, useRef } from 'react';
import { ArrowLeft, Download, Play, ExternalLink } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const videos = [
  { id: 'n96-1', title: 'N96 – 1. Power On/Off', src: 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/2ninrrtx_N96%20-%201.%20Power%20On%3AOff.mp4' },
  { id: 'n96-2', title: 'N96 – 2. Connect Terminal to Wi-Fi', src: 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/3r10kdz9_N96%20-%202.%20Connect%20Terminal%20to%20Wi-Fi.mp4' },
  { id: 'n96-3', title: 'N96 – 3. Process a Sale', src: 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/e4m7dygc_N96%20-%203.%20Process%20a%20Sale.mp4' },
  { id: 'n96-4', title: 'N96 – 4. How to Settle', src: 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/ia7aa3ip_N96%20-%204.%20How%20to%20Settle.mp4' },
  { id: 'n96-5', title: 'N96 – 5. How to Process a Refund', src: 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/k0z6xv7m_N96%20-%205.%20How%20to%20Process%20a%20Refund.mp4' },
];

const VideoCard = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setPlaying(true);
    setTimeout(() => {
      if (videoRef.current) videoRef.current.play();
    }, 100);
  };

  return (
    <div className="flex flex-col" data-testid={`video-card-${video.id}`}>
      <div className="relative aspect-video bg-gray-200 overflow-hidden">
        {playing ? (
          <video
            ref={videoRef}
            src={video.src}
            controls
            className="absolute inset-0 w-full h-full object-contain bg-black"
            data-testid={`video-player-${video.id}`}
          />
        ) : (
          <button
            onClick={handlePlay}
            className="absolute inset-0 w-full h-full flex items-center justify-center group cursor-pointer"
            data-testid={`video-play-${video.id}`}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-gray-200 via-gray-150 to-gray-200" />
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#1F4E79]" />
            <div className="relative z-10 w-14 h-14 rounded-full bg-gradient-to-br from-[#1F4E79] to-[#163a5f] flex items-center justify-center group-hover:from-[#163a5f] group-hover:to-[#0f2d4a] group-hover:scale-110 transition-all duration-300 shadow-lg">
              <Play size={24} className="text-white ml-1" fill="white" />
            </div>
          </button>
        )}
      </div>
      <p className="mt-3 text-sm text-gray-700 font-bold" data-testid={`video-title-${video.id}`}>
        {video.title}
      </p>
    </div>
  );
};

const N96TerminalGuide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pdfUrl = '/assets/ecomm-n96-terminal-guide.pdf';

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="n96-guide-page">
      <Header />

      <div className="h-20" />

      {/* Intro Section */}
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        {(() => {
          const navState = location.state || {};
          const fromTopNav = navState.from === 'top-nav';
          const backLabel = fromTopNav ? 'Back to Home' : (navState.backLabel || 'Back');
          const handleBack = () => {
            if (fromTopNav) navigate('/');
            else if (navState.backPath) navigate(navState.backPath);
            else if (window.history.length > 1) navigate(-1);
            else navigate('/');
          };
          return (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-6 transition-colors duration-200"
              data-testid="n96-back-button"
            >
              <ArrowLeft size={18} />
              {backLabel}
            </button>
          );
        })()}

        <h1
          className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
          data-testid="n96-guide-title"
        >
          N96 Terminal User Guide for Android
        </h1>

        <p className="text-gray-600 text-base sm:text-lg leading-relaxed max-w-4xl" data-testid="n96-guide-description">
          The N96 is a high-performance smart mobile POS terminal, built on a distinctive and forward-thinking product architecture. Featuring a generous 6.5-inch HD display and powered by a 7.6V 3050mAh battery for prolonged standby capability, its refined 64.5mm profile delivers an outstanding payment experience for merchants. Use this guide to help you make the most of the terminal and enjoy great sales in your business. This user manual is also available to{' '}
          <a
            href={pdfUrl}
            download="eCOMM-N96-Terminal-User-Guide.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#1F4E79] font-semibold underline hover:text-[#163a5f] transition-colors duration-200 inline-flex items-center gap-1"
            data-testid="n96-download-link"
          >
            download
            <Download size={16} />
          </a>.
        </p>
      </div>

      {/* Embedded PDF Viewer — DESKTOP / TABLET ONLY.
          Mobile browsers (iOS Safari especially) only render page 1 of a PDF
          inside an iframe, so on phones we hide it and show a "View Guide"
          CTA below that opens the PDF in a new tab. */}
      <div className="w-full px-4 sm:px-6 lg:px-8 pb-20 hidden md:block">
        <div className="max-w-4xl mx-auto w-full" data-testid="n96-pdf-container">
          <iframe
            src={pdfUrl}
            title="N96 Terminal User Guide"
            className="w-full border border-gray-200 shadow-sm"
            style={{ height: '70vh', minHeight: '500px' }}
            data-testid="n96-pdf-viewer"
          />
        </div>
      </div>

      {/* Mobile-only PDF CTA — opens guide in a new tab so users can
          close the tab to return to the site naturally. */}
      <div className="w-full px-4 sm:px-6 pb-16 md:hidden" data-testid="n96-pdf-mobile-cta">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 flex flex-col items-start gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">N96 Terminal User Guide</h3>
            <p className="text-sm text-gray-600">
              Tap below to open the full guide in a new tab — close that tab when you're done to come back here.
            </p>
          </div>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#1F4E79] text-white text-sm font-semibold rounded-lg hover:bg-[#163a5f] active:bg-[#0f2d4a] transition-colors duration-200"
            data-testid="n96-mobile-view-pdf"
          >
            View Full Guide (PDF)
            <ExternalLink size={16} />
          </a>
        </div>
      </div>

      {/* Video Guides Section */}
      <div className="w-full px-4 sm:px-6 lg:px-8 pb-16" data-testid="n96-video-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" data-testid="n96-video-section-title">
            How to Use Your Card Machine
          </h2>
          <p className="text-lg text-gray-600 font-bold mb-10" data-testid="n96-video-section-subtitle">
            Video Guides N96 Terminal for Android<sup>&reg;</sup>
          </p>

          {/* Row 1: Videos 1-3 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {videos.slice(0, 3).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {/* Row 2: Videos 4-5 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.slice(3).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </div>
      </div>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default N96TerminalGuide;
